import { Tooltip } from '@mui/material'
import { HeartHandshake, Plus, Users, X } from 'lucide-react'
import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useUser } from 'util/UserContext'
import { Issue, Impact, Assessment, Influence, Solution, Feedback, WikiSuggestion, User, Team, Meeting } from 'util/constants'
import api from 'config/axiosConfig'

type EntityColor = 'influence' | 'issue' | 'feedback' | 'user' | 'team'

const entityColorMap = {
    'influence': 'bg-eoe-entity-influence border-eoe-entity-influence',
    'issue': 'bg-eoe-entity-issue border-eoe-entity-issue',
    'feedback': 'bg-eoe-entity-feedback border-eoe-entity-feedback',
    'solution': 'bg-eoe-entity-solution border-eoe-entity-solution',
    'impact-positive': 'bg-eoe-entity-impact-positive border-eoe-entity-impact-positive',
    'impact-negative': 'bg-eoe-entity-impact-negative border-eoe-entity-impact-negative',
    'user': 'bg-eoe-entity-user border-eoe-entity-user',
    'team': 'bg-eoe-entity-team border-eoe-entity-team'
};

interface EntityAvatarProps {
    entity: Issue | Impact | Assessment | Influence | Solution | Feedback | WikiSuggestion | User | Team | Meeting
    entityType: 'issue' | 'impact' | 'assessment' | 'influence' | 'solution' | 'feedback' | 'wiki' | 'user' | 'team' | 'meeting'
    isEntityProfilePage?: boolean
    isEntitySection?: boolean
    className?: string
    parentEntityColor?: EntityColor | any | undefined | null | string
    entityChip?: boolean
    isInputField?: boolean
    showCloseChip?: boolean
    badgeText?: string
    onEntityChipClose?: () => void
    showLink?: boolean
    badgeElement?: React.ReactNode
    isGeneralEntity?: boolean
}

const EntityAvatar: React.FC<EntityAvatarProps> = ({
    entity,
    entityType,
    isEntityProfilePage = false,
    isEntitySection = false,
    className = '',
    entityChip = false,
    isInputField = true,
    showCloseChip = true,
    badgeText,
    onEntityChipClose,
    showLink = false,
    badgeElement,
    isGeneralEntity = false
}) => {
    const IMAGE_CDN = process.env.REACT_APP_IMAGE_CDN
    const [enrichedEntity, setEnrichedEntity] = useState<any>(entity);
    const [isLoading, setIsLoading] = useState(false);
    const { currentUser } = useUser()

    useEffect(() => {
        const enrichEntityData = async () => {
            if (!isInputField && entity && entity.id && 
                ((entityType === 'user') || 
                 (entityType === 'team'))) {
                
                setIsLoading(true);
                try {
                    const response = await api.get(
                        entityType === 'user' 
                            ? `/api/user/id/${entity.id}`
                            : `/api/team/id/${entity.id}`
                    );
                    
                    const enriched = {
                        ...entity,
                        username: entityType === 'user' ? response.data.username : undefined,
                        teamname: entityType === 'team' ? response.data.teamname : undefined,
                        display_name: response.data.display_name,
                        properties_public: response.data.properties_public
                    };
                    
                    if (entityType === 'team' && currentUser) {
                        const response = await api.get(`/api/team/${entity.id}/status`);
                        setEnrichedEntity({...enriched, user_entity_details: response.data});
                        //console.log('TEAM STATUS', response.data);
                    } else if (entityType === 'user' && currentUser) {
                        const response = await api.get(`/api/follow/user/${currentUser?.id}/${entityType}/${entity.id}/status`);
                        setEnrichedEntity({...enriched, user_entity_details: response.data});
                        //console.log('USER STATUS', response.data);
                    } else {
                        setEnrichedEntity(enriched);
                    }
                } catch (error) {
                    console.error(`Error fetching ${entityType} details:`, error);
                } finally {
                    setIsLoading(false);
                }
            } else {
                setEnrichedEntity(entity);
            }
        };

        enrichEntityData();
    }, [entity, entityType, isInputField, currentUser]);

    const getEntityDetails = () => {
        const entityData = isInputField ? enrichedEntity : enrichedEntity;
        //console.log('entityData', entityData);
        //console.log('enrichedEntity', enrichedEntity);
        
        switch (entityType) {
            case 'issue':
                if ((entityData as WikiSuggestion).type === 'wiki') {
                    return {
                        title: (entityData as WikiSuggestion).name,
                        subtitle: (entityData as WikiSuggestion).short_description,
                        thumbnail: (entityData as WikiSuggestion).thumbnail || 'https://upload.wikimedia.org/wikipedia/en/8/80/Wikipedia-logo-v2.svg',
                        link: (entityData as WikiSuggestion).link,
                        subLink: '',
                        username: null,
                        userAvatar: null,
                        parentEntityType: 'Issue',
                        parentEntityColor: 'eoe-entity-issue'
                    } 
                }   else {
                        return {
                            title: (entityData as Issue).name,
                            subtitle: (entityData as Issue).properties?.subject_properties?.wiki_parse_wikibase_shortdesc,
                            separator: '~',
                            thumbnail: (entityData as Issue).properties?.subject_properties?.reference_link_img_key ? `${IMAGE_CDN}/${(entityData as Issue).properties?.subject_properties?.reference_link_img_key}` : (entityData as Issue).properties?.subject_properties?.thumbnail?.source || (entityData as Issue).properties?.subject_properties?.wiki_parse_first_img || 'https://upload.wikimedia.org/wikipedia/en/8/80/Wikipedia-logo-v2.svg',
                            link: `/issue/${(entityData as Issue).cname}`,
                            subLink: '',
                            username: (entityData as Issue).username && (entityData as Issue).username.includes('deleted-') ? '[deleted]' : (entityData as Issue).username,
                            userAvatar: (entityData as Issue).properties_public?.avatar_key ? `${IMAGE_CDN}/${(entityData as Issue).properties_public?.avatar_key}` : null,
                            parentEntityType: 'Issue',
                            parentEntityColor: 'issue'
                        }
                    }
            case 'impact':
                return {
                    title: (entityData as Impact).subject_cname ? (entityData as Impact).subject_cname.replace(/_/g, ' ') : (entityData as Impact).name || null,
                    subtitle: (entityData as Impact).topic_cname ? (entityData as Impact).topic_cname.replace(/_/g, ' ') : null,
                    separator: (entityData as Impact).entity_type === 'issue' ? '~' : ' ❯ ',
                    thumbnail: (entityData as Impact).entity_type === 'issue' ? ((entityData as Impact).entity_properties?.subject_properties?.reference_link_img_key ? `${IMAGE_CDN}/${(entityData as Impact).entity_properties?.subject_properties?.reference_link_img_key}` : (entityData as Impact).entity_properties?.subject_properties?.thumbnail?.source || (entityData as Impact).entity_properties?.subject_properties?.wiki_parse_first_img || 'https://upload.wikimedia.org/wikipedia/en/8/80/Wikipedia-logo-v2.svg') : ((entityData as Impact).entity_properties.influencer_properties?.subject_properties?.reference_link_img_key ? `${IMAGE_CDN}/${(entityData as Impact).entity_properties.influencer_properties?.subject_properties?.reference_link_img_key}` : (entityData as Impact).entity_properties.influencer_properties?.subject_properties?.thumbnail?.source || (entityData as Impact).entity_properties.influencer_properties?.subject_properties?.wiki_parse_first_img || 'https://upload.wikimedia.org/wikipedia/en/8/80/Wikipedia-logo-v2.svg'),
                    link: (entityData as Impact).entity_type === 'issue' ? `/issue/${(entityData as Impact).cname}` : `/influence/${(entityData as Impact).influence_id}`,
                    subLink: '',
                    username: (entityData as Impact).username.includes('deleted-') ? '[deleted]' : (entityData as Impact).username,
                    userAvatar: (entityData as Impact).properties_public?.avatar_key ? `${IMAGE_CDN}/${(entityData as Impact).properties_public?.avatar_key}` : null,
                    parentEntityType: (entityData as Impact).entity_type === 'issue' ? 'Issue' : 'Influence',
                    parentEntityColor: (entityData as Impact).entity_type === 'issue' ? 'issue' : 'influence'
                }
            case 'assessment':
                return {
                    title: (entityData as Assessment).influencer_name,
                    subtitle: (entityData as Assessment).influenced_name,
                    separator: `❯`,
                    thumbnail: (entityData as Assessment).influencer_properties?.reference_link_img_key ? `${IMAGE_CDN}/${(entityData as Assessment).influencer_properties?.reference_link_img_key}` : (entityData as Assessment).influencer_properties?.subject_properties?.thumbnail?.source,
                    link: `/influence/${(entityData as Assessment).entity_id}`,
                    sublink: '',
                    username: (entityData as Assessment).username.includes('deleted-') ? '[deleted]' : (entityData as Assessment).username,
                    userAvatar: (entityData as Assessment).properties_public?.avatar_key ? `${IMAGE_CDN}/${(entityData as Assessment).properties_public?.avatar_key}` : null,
                    parentEntityType: 'Influence',
                    parentEntityColor: 'influence'
                }
            case 'influence':
                if (entityChip) {
                    return {
                        title: (entityData as Influence).influencer_name,
                        subtitle: (entityData as Influence).influenced_name,
                        separator: `❯`,
                        thumbnail: (entityData as Influence).influencer_properties?.reference_link_img_key ? `${IMAGE_CDN}/${(entityData as Influence).influencer_properties?.reference_link_img_key}` : (entityData as Influence).influencer_properties?.subject_properties?.thumbnail?.source,
                        link: `/issue/${(entityData as Influence).influencer_name.replace(/ /g, '_')}`,
                        subLink: `/issue/${(entityData as Influence).influenced_name.replace(/ /g, '_')}`,
                        username: (entityData as Influence).username.includes('deleted-') ? '[deleted]' : (entityData as Influence).username,
                        userAvatar: (entityData as Influence).properties_public?.avatar_key ? `${IMAGE_CDN}/${(entityData as Influence).properties_public?.avatar_key}` : null,
                        parentEntityType: 'Issue',
                        parentEntityColor: 'issue'
                    }
                } else {
                    return {
                        title: (entityData as Influence).influencer_name,
                        subtitle: (entityData as Influence).influenced_name,
                        separator: `❯`,
                        thumbnail: (entityData as Influence).influencer_properties?.reference_link_img_key ? `${IMAGE_CDN}/${(entityData as Influence).influencer_properties?.reference_link_img_key}` : (entityData as Influence).influencer_properties?.subject_properties?.thumbnail?.source,
                        link: `/issue/${(entityData as Influence).influencer_name.replace(/ /g, '_')}`,
                        subLink: `/issue/${(entityData as Influence).influenced_name.replace(/ /g, '_')}`,
                        username: (entityData as Influence).username.includes('deleted-') ? '[deleted]' : (entityData as Influence).username,
                        userAvatar: (entityData as Influence).properties_public?.avatar_key ? `${IMAGE_CDN}/${(entityData as Influence).properties_public?.avatar_key}` : null,
                        parentEntityType: 'Issue',
                        parentEntityColor: 'issue'
                    }
                }
            case 'solution':
                return {
                    title: (entityData as Solution).related_issues[0].subject_cname.replace(/_/g, ' '),
                    subtitle: (entityData as Solution).related_issues[0].topic_cname ? (entityData as Solution).related_issues[0].topic_cname.replace(/_/g, ' ') : undefined,
                    separator: '~',
                    thumbnail: (entityData as Solution).related_issues[0].properties?.reference_link_img_key ? `${IMAGE_CDN}/${(entityData as Solution).related_issues[0].properties?.reference_link_img_key}` : (entityData as Solution).related_issues[0].properties?.subject_properties?.thumbnail?.source,
                    link: `/issue/${(entityData as Solution).related_issues[0].cname}`,
                    username: (isGeneralEntity ? (entityData as Solution).name : (entityData as Solution).username.includes('deleted-') ? '[deleted]' : (entityData as Solution).username),
                    userAvatar: (entityData as Solution).properties_public?.avatar_key ? `${IMAGE_CDN}/${(entityData as Solution).properties_public?.avatar_key}` : null,
                    parentEntityType: 'Issue',
                    parentEntityColor: 'issue',
                    entityType: (entityData as Solution)?.type
                }
            case 'feedback':
                return {
                    title: (entityData as Feedback).username,
                    thumbnail: (entityData as Feedback).properties_public?.avatar_key ? `${IMAGE_CDN}/${(entityData as Feedback).properties_public?.avatar_key}` : null,
                    link: `/feedback/${(entityData as Feedback).id}`,
                    username: (entityData as Feedback).username.includes('deleted-') ? '[deleted]' : (entityData as Feedback).username,
                    userAvatar: (entityData as Feedback).properties_public?.avatar_key ? `${IMAGE_CDN}/${(entityData as Feedback).properties_public?.avatar_key}` : null,
                    parentEntityType: 'Feedback',
                    parentEntityColor: 'feedback'
                }
            case 'user':
                return {
                    title: entityData.username || entityData.display_name || `User ${entityData.id}`,
                    subtitle: entityData.display_name,
                    thumbnail: entityData.properties_public?.avatar_key ? `${IMAGE_CDN}/${entityData.properties_public.avatar_key}` : null,
                    link: `/user/${entityData.username || entityData.id}`,
                    subLink: '',
                    username: (entityData as User).username,
                    userAvatar: (entityData as User).properties_public?.avatar_key ? `${IMAGE_CDN}/${(entityData as User).properties_public.avatar_key}` : null,
                    parentEntityType: 'User',
                    parentEntityColor: 'user',
                    userEntityDetails: entityData.user_entity_details
                };
            case 'team':
                return {
                    title: entityData.teamname || entityData.display_name || `Team ${entityData.id}`,
                    subtitle: entityData.display_name,
                    thumbnail: entityData.properties_public?.avatar_key ? `${IMAGE_CDN}/${entityData.properties_public.avatar_key}` : null,
                    link: `/partea/${entityData.teamname || entityData.id}`,
                    subLink: '',
                    username: (entityData as Team).teamname,
                    userAvatar: (entityData as Team).properties_public?.avatar_key ? `${IMAGE_CDN}/${(entityData as Team).properties_public.avatar_key}` : null,
                    parentEntityType: 'Team',
                    parentEntityColor: 'team',
                    userEntityDetails: entityData.user_entity_details
                };
            default:
                return {
                    title: '',
                    thumbnail: undefined,
                    link: '/',
                    subLink: '',
                    username: '',
                    userAvatar: null,
                    parentEntityType: '',
                    parentEntityColor: 'eoe-red'
                }
        }
    }
    const { title, subtitle, thumbnail, link, subLink, username, userAvatar, separator, parentEntityType, parentEntityColor, userEntityDetails } = getEntityDetails()
      
    if (isLoading) {
        return (
            <div className="animate-pulse">
                <div className="w-9 h-9 bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover rounded-full"></div>
            </div>
        );
    }
    
    if (isEntityProfilePage && entityType !== 'influence') {
        return (
        <div className={`flex min-w-0 pr-2 items-center gap-1 text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub ${className}`}>           
            <Link
                to={link}
            >
                <div 
                    className={`w-9 h-9 rounded-full flex-shrink-0 overflow-hidden bg-transparent dark:bg-transparent border-[3px] border-eoe-icon-hover dark:border-eoe-dark-icon`}
                >
                    {thumbnail ? (
                        <img
                            alt={title}
                            src={thumbnail}
                            className="w-full h-full object-scale-down"
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-sm text-eoe-gray dark:text-eoe-dark-text">
                            {title.charAt(0).toUpperCase()}
                            {subtitle && (<>{separator}{subtitle?.charAt(0).toUpperCase()}</>)}
                        </div>
                    )}
                </div>
            </Link>
            <div className="flex flex-col min-w-0">
                <div className="group flex flex-row gap-1 items-center min-w-0">
                    <span className="font-semibold leading-none truncate">
                        {entityType === 'feedback' ? (
                        <div className="rounded-full group card-username min-w-0 truncate max-w-[calc(100vw-80px)]">
                            <Tooltip title={`View ${username}'s profile`} arrow>
                                <Link to={`/user/${username}`} className="card-username truncate">
                                    <span className="card-username truncate">{username}</span>
                                </Link>
                            </Tooltip>
                        </div>
                        ) : (
                            <Link to={link} className={`group-hover:underline`}>
                                {title}
                            </Link>
                        )}
                        {subtitle && (
                            <>
                                
                                <Link to={link} className="group-hover:underline">
                                    {separator}
                                </Link>
                                <Link to={subLink || link} className={`group-hover:underline`}>
                                    {subtitle}
                                </Link>
                            </>
                        )}
                    </span>
                    <span className={`text-xs font-medium rounded-full ${entityColorMap?.[parentEntityColor as EntityColor]} text-white px-1.5 hover:no-underline group-hover:no-underline whitespace-nowrap flex-shrink-0 ${entityType === 'feedback' ? 'hidden' : ''}`}>
                        {parentEntityType}
                    </span>
                </div>
                    {username && entityType !== 'feedback' && (
                        <div className="rounded-full group card-username min-w-0 truncate max-w-[calc(100vw-80px)]">
                            {username && isGeneralEntity ? (
                                <Tooltip title={`View this ${entityType}`} arrow>
                                    <Link to={`/${entityType}/${entity.id}`} className="card-username truncate">
                                        <span className="text-sm font-medium card-username truncate">{username}</span>
                                    </Link>
                                </Tooltip>
                            ) : (
                                <Tooltip title={`View ${username}'s profile`} arrow>
                                    <Link to={`/user/${username}`} className="card-username truncate">
                                        <span className="text-sm font-medium card-username truncate">{username}</span>
                                    </Link>
                                </Tooltip>
                            )}
                            {badgeText && (
                                <span className={`flex rounded-full px-1.5 text-xs flex-shrink-0 ${entityColorMap?.[entityType as EntityColor]} text-white no-underline hover:no-underline group-hover:no-underline whitespace-nowrap font-normal`}>{badgeText}</span>
                            )}
                            {badgeElement && (
                                <div className="flex-shrink-0 text-sm leading-none">
                                    {badgeElement}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        )
    }

    else if (isEntitySection && entityType !== 'influence') {
        return ( 
            <Tooltip title={`View ${username}'s profile`} arrow>           
                <Link
                    to={`/user/${username}`}
                >
                    <div className={`group flex flex-row items-center pr-2 gap-1 text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub ${className}`}>
                        <div 
                            className="w-8 h-8 rounded-full flex-shrink-0 overflow-hidden bg-transparent dark:bg-transparent border-[3px] border-eoe-icon-hover dark:border-eoe-dark-icon"
                        >
                            {userAvatar && userAvatar !== undefined ? (
                                <img
                                    alt={username}
                                    src={userAvatar}
                                    className="w-full h-full object-scale-down"
                                />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-sm text-eoe-gray dark:text-eoe-dark-text">
                                    {username?.charAt(0).toUpperCase()}
                                </div>
                            )}
                        </div>
                            {username && (
                                <div className="items-center rounded-full group card-username max-w-[calc(100vw-80px)] truncate">
                                    <span className={`font-medium card-username truncate ${entityType === 'feedback' ? 'font-semibold' : 'text-sm'}`}>{username}</span>
                                </div>
                            )}
                    </div>
                </Link>
        </Tooltip>
        )
    }

    else if (entityChip && !showLink) {
        return (
            <div className={`group flex flex-row min-w-0 items-center pr-2 gap-1 text-sm rounded-full pl-1 py-1 bg-eoe-bg dark:bg-eoe-dark-bg-modal text-eoe-text-sub dark:text-eoe-dark-text-sub ${className}`}>
                <div 
                    className={`w-9 h-9 rounded-full flex-shrink-0 overflow-hidden bg-transparent dark:bg-transparent border-[3px] border-eoe-icon-hover dark:border-eoe-dark-icon`}
                >
                    {thumbnail ? (
                        <img
                            alt={title}
                            src={thumbnail}
                            className="w-full h-full object-scale-down"
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-sm text-eoe-gray dark:text-eoe-dark-gray">
                            {title.charAt(0).toUpperCase()}
                            {subtitle && (<>{separator}{subtitle?.charAt(0).toUpperCase()}</>)}
                        </div>
                    )}
                </div>
                <div className={`flex flex-col justify-start gap-1 min-w-0 flex-grow`}>
                    <div className="group flex flex-row gap-1 justify-between items-center min-w-0">
                        <span className="flex flex-row gap-0.5 font-semibold text-sm leading-none truncate">
                                {entityType === 'team' && (
                                    <span className="flex-shrink-0">
                                        <Users className={`size-3.5 ${userEntityDetails ? 'text-green-600' : ''}`} />
                                    </span>
                                )}
                                {userEntityDetails && (<>
                                        {userEntityDetails.isBestea && (
                                            <span className="flex-shrink-0">
                                                <HeartHandshake className="size-3.5 text-green-600" />
                                            </span>
                                        )}
                                </>)}
                            {title}
                            {badgeText && (
                                <span className={`flex rounded-full px-1 text-xs flex-shrink-0 ${entityColorMap?.[parentEntityColor as EntityColor]} text-white no-underline hover:no-underline group-hover:no-underline whitespace-nowrap flex-shrink-0`}>{badgeText}</span>
                            )}
                            {badgeElement && (
                                <div className="flex-shrink-0 text-sm leading-none">
                                    {badgeElement}
                                </div>
                            )}
                        </span>
                    </div>
                    {subtitle && (
                        <div className="flex flex-row gap-1 items-center min-w-0 flex-shrink">
                            <span className="text-xs leading-none truncate">
                                {subtitle}
                            </span>
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0 ml-auto">
                    {(entity as WikiSuggestion).type === 'wiki' && (
                        <div className="flex items-center space-x-0 p-1 rounded-full border text-eoe-gray dark:text-eoe-dark-text"><img src='https://upload.wikimedia.org/wikipedia/en/8/80/Wikipedia-logo-v2.svg' alt='Wikipedia Logo' className="w-5 h-5 rounded-full text-eoe-gray dark:text-eoe-dark-text" /><Plus className="w-5 h-5 rounded-full text-eoe-gray dark:text-eoe-dark-text" /></div>
                    )}
                    {showCloseChip && (
                        <button onClick={onEntityChipClose} className="flex-shrink-0 size-5 flex justify-center items-center rounded-full border border-eoe-text-sub dark:border-eoe-dark-text-sub text-eoe-text-sub dark:text-eoe-dark-text-sub mr-2">
                            <X className="size-4" />
                        </button>
                    )}
                </div>
            </div>
        )
    }

    else if (entityChip && showLink) {
        return (
            <Link
                to={link}
                className={`min-w-0 max-w-fit`}
            >          
                <div className={`group flex flex-row min-w-0 items-center pr-2 gap-1 text-sm rounded-full pl-1 py-1 bg-eoe-bg dark:bg-eoe-dark-bg-modal text-eoe-text-sub dark:text-eoe-dark-text-sub ${className}`}>
                    <div 
                        className={`w-9 h-9 rounded-full flex-shrink-0 overflow-hidden bg-transparent dark:bg-transparent border-[3px] border-eoe-icon-hover dark:border-eoe-dark-icon`}
                    >
                        {thumbnail ? (
                            <img
                                alt={title}
                                src={thumbnail}
                                className="w-full h-full object-scale-down"
                            />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-sm text-eoe-gray dark:text-eoe-dark-gray">
                                {title.charAt(0).toUpperCase()}
                                {subtitle && (<>{separator}{subtitle?.charAt(0).toUpperCase()}</>)}
                            </div>
                        )}
                    </div>
                    <div className={`flex flex-col justify-start gap-1 min-w-0 flex-grow`}>
                        <div className="group flex flex-row gap-1 items-center min-w-0">
                                {entityType === 'team' && (
                                    <span className="flex-shrink-0 leading-none">
                                        <Users className={`size-3.5 ${userEntityDetails ? 'text-green-600' : ''}`} />
                                    </span>
                                )}
                                {userEntityDetails && (<>
                                        {userEntityDetails.isBestea && (
                                            <span className="flex-shrink-0 leading-none">
                                                <HeartHandshake className="size-3.5 text-green-600" />
                                            </span>
                                        )}
                                </>)}
                                
                                <span className="font-semibold leading-none truncate group-hover:underline hover:underline">{title}</span>
                                {badgeText && (
                                    <span className={`flex rounded-full px-1 text-xs flex-shrink-0 ${entityColorMap?.[parentEntityColor as EntityColor]} text-white no-underline hover:no-underline group-hover:no-underline whitespace-nowrap`}>{badgeText}</span>
                                )}
                                {badgeElement && (
                                    <div className="flex-shrink-0 text-sm leading-none">
                                        {badgeElement}
                                    </div>
                                )}
                        </div>
                        {subtitle && (
                            <div className="flex flex-row gap-1 items-center min-w-0 flex-shrink">
                                <span className="text-xs leading-none truncate">
                                    {subtitle}
                                </span>
                            </div>
                        )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0 ml-auto">
                        {(entity as WikiSuggestion).type === 'wiki' && (
                            <div className="flex items-center space-x-0 p-1 rounded-full border text-eoe-gray dark:text-eoe-dark-text"><img src='https://upload.wikimedia.org/wikipedia/en/8/80/Wikipedia-logo-v2.svg' alt='Wikipedia Logo' className="w-5 h-5 rounded-full text-eoe-gray dark:text-eoe-dark-text" /><Plus className="w-5 h-5 rounded-full text-eoe-gray dark:text-eoe-dark-text" /></div>
                        )}
                        {showCloseChip && (
                            <button onClick={onEntityChipClose} className="flex-shrink-0 size-5 flex justify-center items-center rounded-full border border-eoe-text-sub dark:border-eoe-dark-text-sub text-eoe-text-sub dark:text-eoe-dark-text-sub mr-2">
                                <X className="size-4" />
                            </button>
                        )}
                    </div>
                </div>
            </Link>
        )
    }

    else {
        return (
            <div className={`flex min-w-0 items-center pr-2 gap-1 text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub ${className}`}>
                <Link
                    to={link}
                    className={`flex items-center gap-1 text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub ${className}`}
                >
                    <div 
                        className={`w-9 h-9 rounded-full flex-shrink-0 overflow-hidden bg-transparent dark:bg-transparent border-[3px] border-eoe-icon-hover dark:border-eoe-dark-icon`}
                    >
                        {thumbnail ? (
                            <img
                                alt={title}
                                src={thumbnail}
                                className="w-full h-full object-scale-down"
                            />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-sm text-eoe-gray dark:text-eoe-dark-gray">
                                {title.charAt(0).toUpperCase()}
                                {subtitle && (<>{separator}{subtitle?.charAt(0).toUpperCase()}</>)}
                            </div>
                        )}
                    </div>
                </Link>
                <div className="flex flex-col min-w-0">
                    {entityType !== 'influence' ? (
                        <Link to={link} className={`group`}>
                            <div className="group flex flex-row gap-1 items-center min-w-0">
                                <span className="font-semibold leading-none truncate group-hover:underline hover:underline">
                                        {title}
                                    {subtitle && (
                                        <>
                                            <span>{separator}</span>
                                            <span>{subtitle}</span>
                                        </>
                                    )}
                                </span>
                                <span className={`text-xs font-medium rounded-full ${entityColorMap?.[parentEntityColor as EntityColor]} text-white px-1.5 no-underline hover:no-underline group-hover:no-underline whitespace-nowrap flex-shrink-0`}>
                                    {parentEntityType}
                                </span>
                            </div>
                        </Link>
                    ) : (
                        <div className="flex flex-col justify-start gap-1 min-w-0">
                            <Link to={link} className={`group`}>
                                <div className="group flex flex-row gap-1 items-center">
                                    <span className="font-semibold text-sm leading-none truncate group-hover:underline hover:underline">
                                            {title}
                                    </span>
                                    <span className={`text-xs font-medium rounded-full ${entityColorMap?.[parentEntityColor as EntityColor]} text-white px-1.5 hover:no-underline group-hover:no-underline no-underline whitespace-nowrap flex-shrink-0`}>
                                        Influencer
                                    </span>
                                </div>
                            </Link>
                            {subtitle && (
                                <Link to={subLink || link} className={`group`}>
                                    <div className="flex flex-row gap-1 items-center min-w-0">
                                        <span className="text-xs font-semibold leading-none truncate hover:underline group-hover:underline">
                                                {subtitle}
                                        </span>
                                        <span className={`text-xs font-medium rounded-full ${entityColorMap?.[parentEntityColor as EntityColor]} text-white px-1.5 hover:no-underline group-hover:no-underline no-underline whitespace-nowrap flex-shrink-0`}>
                                            Influenced
                                        </span>
                                    </div>
                                </Link>
                            )}
                        </div>
                    )}
                    {username && entityType !== 'influence' && (
                        <div className="flex items-center rounded-full group card-username">
                            <Tooltip title={`View ${username}'s profile`} arrow>
                                <Link to={`/user/${username}`} className="card-username truncate">
                                    <span className="text-sm font-medium">{username}</span>
                                </Link>
                            </Tooltip>
                                {badgeText && (
                                    <span className={`flex rounded-full px-1 text-xs flex-shrink-0 ${entityColorMap?.[parentEntityColor as EntityColor]} text-white no-underline hover:no-underline group-hover:no-underline whitespace-nowrap`}>{badgeText}</span>
                                )}
                                {badgeElement && (
                                    <div className="flex-shrink-0 text-sm leading-none">
                                        {badgeElement}
                                    </div>
                                )}
                        </div>
                    )}
                </div>
            </div>
        )
    }
}

export default EntityAvatar